'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component, createRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import UUID from '../utils/UUID';
import RefType from '../utils/RefType';
import { getOrigin, getIFrameSrc } from '../utils/IFrameSrc';
import { createAppData, createEmbeddedPropsChangeMessage, createHostContext, createSyncMessage, isReadyMessage, isToIFrameHost, validMessage } from 'ui-addon-iframeable/messaging/IFrameControlMessage';
import { logHostError, logHostMessageReceived, logHostMessageSent } from 'ui-addon-iframeable/utils/Logger';
import { isMessageToMe } from 'ui-addon-iframeable/messaging/IFrameMessage';
import { ERROR_EMBED_NOT_READY, ERROR_INVALID_CALLBACK_PROVIDED, ERROR_UNABLE_TO_SYNC } from 'ui-addon-iframeable/messaging/Errors';

class UIIFrame extends Component {
  constructor(props) {
    super(props);
    this.handleReadyTimeout = this.handleReadyTimeout.bind(this);
    this.handleReceiveMessage = this.handleReceiveMessage.bind(this);
    this.handleSyncAckMessage = this.handleSyncAckMessage.bind(this);
    this.retrySendSync = this.retrySendSync.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
    this.callbacks = {};
    this.syncRetryInterval = 100;
    this.maxNumSyncRetries = props.initTimeout / this.syncRetryInterval;
    this.numSyncRetries = 0;
    const iFrameSrc = getIFrameSrc(props.name, props.src);
    this.iFrame = {
      origin: getOrigin(iFrameSrc),
      src: iFrameSrc,
      ref: props.innerRef || /*#__PURE__*/createRef(),
      synced: false,
      ready: false,
      uuid: UUID()
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.handleReceiveMessage, false);
    this.sendSync();
  }

  shouldComponentUpdate(nextProps) {
    if (this.props.embeddedPassthruProps !== nextProps.embeddedPassthruProps) {
      this.handleEmbeddedPassthruPropsChange(nextProps.embeddedPassthruProps);
    }

    return this.props.className !== nextProps.className || this.props.height !== nextProps.height || this.props.iframePassthruProps !== nextProps.iframePassthruProps || this.props.scrolling !== nextProps.scrolling || this.props.src !== nextProps.src || this.props.width !== nextProps.width;
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleReceiveMessage);
  }

  hasCallback(message) {
    return !!this.callbacks[message.id];
  }

  validCallback(callback) {
    if (typeof callback === 'function') {
      return true;
    }

    logHostError(this.props.name, ERROR_INVALID_CALLBACK_PROVIDED, JSON.stringify(callback));
    return false;
  }

  isFromEmbed(message) {
    return message.from === this.iFrame.uuid;
  }

  isToEmbed(message) {
    return isMessageToMe(message, this.iFrame.uuid, this.props.group);
  }

  dispatchCallback(message) {
    this.callbacks[message.id](message.payload);
    delete this.callbacks[message.id];
  }

  sendMessage(message, callback) {
    if (!this.iFrame.ref.current || !this.iFrame.ref.current.contentWindow) {
      return;
    }

    if (callback && this.validCallback(callback)) {
      this.callbacks[message.id] = callback;
    }

    this.iFrame.ref.current.contentWindow.postMessage(message, this.iFrame.origin);
    logHostMessageSent(this.props.name, message);
  }

  handleEmbeddedPassthruPropsChange(embeddedProps) {
    const embeddedPropsChangeMessage = createEmbeddedPropsChangeMessage(this.iFrame.uuid, embeddedProps);
    this.sendMessage(embeddedPropsChangeMessage);
  }

  handleReceiveMessage(event) {
    if (!validMessage(event, this.iFrame.origin)) {
      return;
    }

    const {
      data: message
    } = event;
    logHostMessageReceived(this.props.name, message);

    if (this.hasCallback(message)) {
      this.dispatchCallback(message);
    } else if (this.isFromEmbed(message)) {
      if (isReadyMessage(message)) {
        this.handleIFrameReady(message);
      } else if (isToIFrameHost(message)) {
        this.props.onMessage(message);
      }
    } else if (this.isToEmbed(message)) {
      this.sendMessage(message);
    }
  }

  handleIFrameReady(message) {
    this.iFrame.ready = true;
    this.props.onReady(createHostContext(this.iFrame.uuid, this.sendMessage), message.payload);
  }

  handleInitTimeout() {
    const {
      name,
      onInitError
    } = this.props;
    logHostError(name, ERROR_UNABLE_TO_SYNC);
    onInitError(ERROR_UNABLE_TO_SYNC);
  }

  handleReadyTimeout() {
    if (this.iFrame.ready || !this.iFrame.ref.current) {
      return;
    }

    const {
      name,
      onReadyError
    } = this.props;
    logHostError(name, ERROR_EMBED_NOT_READY);
    onReadyError(ERROR_EMBED_NOT_READY);
  }

  handleSyncAckMessage() {
    this.iFrame.synced = true;
    setTimeout(this.handleReadyTimeout, this.props.readyTimeout);
  }

  retrySendSync() {
    if (this.numSyncRetries === this.maxNumSyncRetries) {
      this.handleInitTimeout();
      return;
    }

    if (!this.iFrame.synced && this.iFrame.ref.current) {
      this.sendSync();
    }
  }

  sendSync() {
    const {
      appInfo,
      appName,
      embeddedPassthruProps,
      group,
      name
    } = this.props;
    const appData = createAppData(appInfo, appName);
    const syncMessage = createSyncMessage({
      embedId: this.iFrame.uuid,
      group,
      name,
      appData,
      embeddedProps: embeddedPassthruProps
    });
    this.sendMessage(syncMessage, this.handleSyncAckMessage);
    this.numSyncRetries++;
    setTimeout(this.retrySendSync, this.syncRetryInterval);
  }

  render() {
    const {
      className,
      height,
      iframePassthruProps,
      scrolling,
      width
    } = this.props;
    return /*#__PURE__*/_jsx("iframe", Object.assign({
      frameBorder: 0
    }, iframePassthruProps, {
      className: className,
      height: String(height),
      ref: this.iFrame.ref,
      scrolling: scrolling ? 'yes' : 'no',
      src: this.iFrame.src,
      width: String(width)
    }));
  }

}

UIIFrame.propTypes = {
  appInfo: PropTypes.object.isRequired,
  appName: PropTypes.string.isRequired,
  className: PropTypes.string,
  embeddedPassthruProps: PropTypes.object,
  group: PropTypes.string.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  iframePassthruProps: PropTypes.object,
  initTimeout: PropTypes.number.isRequired,
  innerRef: RefType,
  name: PropTypes.string.isRequired,
  onInitError: PropTypes.func.isRequired,
  onMessage: PropTypes.func.isRequired,
  onReady: PropTypes.func.isRequired,
  onReadyError: PropTypes.func.isRequired,
  readyTimeout: PropTypes.number.isRequired,
  scrolling: PropTypes.bool.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
UIIFrame.defaultProps = {
  appInfo: {},
  appName: '',
  className: 'flex-grow-1',
  embeddedPassthruProps: {},
  group: 'iframeable',
  height: 200,
  iframePassthruProps: {},
  initTimeout: 8000,
  name: 'embed',
  onInitError: emptyFunction,
  onMessage: emptyFunction,
  onReadyError: emptyFunction,
  readyTimeout: 3000,
  scrolling: true,
  width: 200
};
export default UIIFrame;
export const UIIFrameWithRef = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(UIIFrame, Object.assign({}, props, {
  innerRef: ref
})));